import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { DOCUMENT } from '@angular/common';

import { VirtualGridColumn } from '../../types/virtual-grid-column';
import {
  ChangeDetector as IChangeDetector,
  FilterComponent,
  FilterController,
  FilterControllerContext
} from '../../filter';
import { DateFilterController } from '../../filter/types/date-filter';
import { RangeFilterController } from '../../filter/types/range-filter';
import { SetFilterController } from '../../filter/types/set-filter';
import { TextFilterController } from '../../filter/types/text-filter';
import { SortCriteria } from '../../types/sort-criteria';
import { TemplateType } from '../../types/template-type';
import { ChangeDetector } from '@wfm/core';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';

class FilterChangeDetector extends ChangeDetector implements IChangeDetector {
  constructor(window: Window, changeDetectorRef: ChangeDetectorRef) {
    super(window, changeDetectorRef);
  }

  detectChanges(): void {
    this.$detectChanges();
  }
}

@Component({
  selector: 'virtual-grid-header-dropdown',
  templateUrl: './virtual-grid-header-dropdown.component.html',
  styleUrls: ['./virtual-grid-header-dropdown.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class VirtualGridHeaderDropdownComponent implements AfterViewInit, OnChanges, OnDestroy {
  /**
   * The parent column of this dropdown component instance.
   */
  @Input()
  column: VirtualGridColumn;

  @Input()
  isHidden = false;

  /**
   * Specifies whether or not this is the active filter.
   */
  @Input()
  isActive: boolean;

  /**
   * If specified, provides the current filter data for the column.
   */
  @Input()
  filterData?: any;

  /**
   * filter configuration
   */
  @Input()
  filterConfig?: any;

  /**
   * filter component
   */
  @Input()
  filterComponent?: FilterComponent;

  /**
   * If specified and true, hides the Clear and Apply buttons.  This is for internal use in the card filter layout.
   */
  @Input()
  cardFilterChild: boolean;

  /**
   * Current sort configuration for the grid.
   */
  @Input()
  sortCriteria: SortCriteria[];

  @Input() accessibleInstitutes: { id: number }[] = [];
  @Input() instituteSeparationEnabled = false;

  /**
   * Emits an event to the host that this component should be de-activated.  If the event data is true, the
   * next filter will be activated.  If the event data is false, the previous filter will be activated.  Otherwise
   * no new filter will be activated.
   */
  @Output()
  closeRequest?: EventEmitter<boolean | undefined> = new EventEmitter<boolean | undefined>();

  /**
   * Emits an event to the host that the filter has changed for the active column.
   */
  @Output()
  filterDataChange?: EventEmitter<any> = new EventEmitter<any>();

  /**
   * Emits an event to the host that the column is now sorted.
   */
  @Output()
  itemSortSelectionChange: EventEmitter<{ column: VirtualGridColumn; ascending: boolean }> = new EventEmitter<{
    column: VirtualGridColumn;
    ascending: boolean;
  }>();

  /**
   * Standard constructor.
   */
  constructor(private cdref: ChangeDetectorRef, @Inject(DOCUMENT) private document: any) {
    this.window = document.defaultView;
    this.filterChangeDetector = new FilterChangeDetector(this.window, this.cdref);
  }

  ngOnDestroy(): void {
    if (this.filterController) {
      this.filterController.destroy();
    }
  }

  getDropdownStyles(): any {
    const style = {
      position: 'relative'
    };

    if (this.cardFilterChild) {
      style['padding'] = '0px';
      // Width controlled by the card filter parent.
    } else {
      style['padding'] = this.column?.sortTemplate ? '0.0px' : '1.2rem';
      if (this.filterController) {
        style['width'] = `${this.filterController.getDropdownWidth()}px`;
      }
    }

    return style;
  }

  /**
   * Angular life cycle hook.
   */
  ngAfterViewInit(): void {
    this.initFilterController();
  }

  /**
   * Angular life cycle hook.
   */
  ngOnChanges(changes: SimpleChanges): void {
    // If this dropdown is now active, re-evaluate the show states.
    if (changes['isActive'] && this.isActive) {
      // Never show a sort section when this component is a child of a card filter.  Otherwise we show the sort
      // section if there is a custom sort template.
      this.showSortSection = !this.cardFilterChild && !!this.column?.sortTemplate;
      // Show the filter section if there is a filter template.
      this.showFilterSection = !!this.filterTemplate;
      this.setRenderedItemToStart();
    }

    // If the 'isActive' input changes, notify the filter controller.
    if (changes['isActive'] && this.filterController) {
      this.window.setTimeout(() => {
        this.filterController.onActivate(this.isActive);
      });
    }

    // If the 'filterData' input changes, re-initialize the controller.
    if (changes['filterData'] && this.filterController) {
      this.window.setTimeout(() => {
        this.filterController.init({
          column: this.column,
          filterData: this.filterData // The new value has been updated.
        });
      });
    }

    // If the 'sortCriteria' input changes, determine if this column is filtered or not.
    if (changes['sortCriteria']) {
      if (this.sortCriteria && this.sortCriteria.length && this.sortCriteria[0].column.id === this.column.id) {
        this.sortedAscending = this.sortCriteria[0].isOrderAscending;
      } else {
        this.sortedAscending = undefined;
      }
    }

    if (
      (changes.column && !changes.column.firstChange) ||
      (changes.filterConfig && !changes.filterConfig.firstChange) ||
      (changes.filterComponent && !changes.filterComponent.firstChange)
    ) {
      this.initFilterController();
    }

    this.detectChanges();
  }

  setRenderedItemToStart() {
    if (this.viewport) {
      const range = this.viewport.getRenderedRange();
      this.viewport.setRenderedContentOffset(0);
      range.end = range.end - range.start;
      range.start = 0;
      this.viewport.setRenderedRange(range);
    }
  }

  detectChanges() {
    this.window.setTimeout(() => {
      this.cdref.detectChanges();
    }, 10);
  }

  /**
   * Event handler for an apply event.
   */
  onApply(): void {
    if (this.doApply()) {
      this.emitCloseRequest();
    }
  }

  /**
   * Event handler for a cancel event.
   */
  onCancel(): void {
    this.doCancel();
    this.emitCloseRequest();
  }

  /**
   * Event handler for a clear event.
   */
  onClear(): void {
    this.doClear();
    // Apply right away and close.
    this.onApply();
  }

  /**
   * Event handler for a click on the 'sort ascending' or 'sort descending' item'.
   */
  onItemSortClicked(column: VirtualGridColumn, ascending: boolean): void {
    this.itemSortSelectionChange.emit({ column: column, ascending: ascending });
  }

  /**
   * Listens for the 'Enter' key to be pressed and maps it to the 'apply' action if this is the active filter.
   */
  @HostListener('window:keydown.enter', ['$event'])
  onEnterKeyEvent(event: KeyboardEvent): void {
    if (this.isActive) {
      event.preventDefault();
      if (this.doApply()) {
        this.blur(event.target);
        this.emitCloseRequest();
      }
    }
  }

  /**
   * Listens for the 'Esc' key to be pressed and maps it to the 'cancel' action if this is the active filter.
   */
  @HostListener('window:keydown.escape', ['$event'])
  onEscapeKeyEvent(event: KeyboardEvent): void {
    if (this.isActive) {
      this.blur(event.target);
      this.onCancel();
    }
  }

  /**
   * Listens for the 'Shift+Tab' key to be pressed and maps it to the 'apply' of the active filter, then activates the previous one.
   */
  @HostListener('window:keydown.shift.tab', ['$event'])
  onShiftTabKeyEvent(event: KeyboardEvent): void {
    if (this.isActive && event.target instanceof HTMLElement) {
      const element = event.target as HTMLInputElement;
      if (element.name?.startsWith('datepicker-end-')) {
        // Skip
      } else {
        event.preventDefault();
        if (this.doApply()) {
          this.blur(event.target);
          this.emitCloseRequest(false);
        }
      }
    }
  }

  /**
   * Listens for the 'Tab' key to be pressed and maps it to the 'apply' of the active filter, then activates the next one.
   */
  @HostListener('window:keydown.tab', ['$event'])
  onTabKeyEvent(event: KeyboardEvent): void {
    if (this.isActive && event.target instanceof HTMLElement) {
      const element = event.target as HTMLInputElement;
      if (element.name?.startsWith('datepicker-start-')) {
        // Skip
      } else {
        event.preventDefault();
        if (this.doApply()) {
          this.blur(event.target);
          this.emitCloseRequest(true);
        }
      }
    }
  }

  @ViewChild('defaultSortTemplate', { read: TemplateRef })
  defaultSortTemplate: TemplateRef<any>;

  /**
   * Reference to the internal text filter template.
   */
  @ViewChild('textFilter', { read: TemplateRef })
  textFilterTemplate: TemplateRef<any>;

  /**
   * Reference to the internal date filter template.
   */
  @ViewChild('dateFilter', { read: TemplateRef })
  dateFilterTemplate: TemplateRef<any>;

  /**
   * Reference to the internal set filter template.
   */
  @ViewChild('setFilter', { read: TemplateRef })
  setFilterTemplate: TemplateRef<any>;

  /**
   * Reference to the internal range filter template.
   */
  @ViewChild('rangeFilter', { read: TemplateRef })
  rangeFilterTemplate: TemplateRef<any>;
  /**
   * Reference to the CdkVirtualScrollViewport.
   */
  @ViewChild(CdkVirtualScrollViewport) viewport: CdkVirtualScrollViewport;

  /**
   * Called in response to a keyboard event, this will remove focus from the control that was the target of the keyboard
   * event.  In the case of an input control, this will cause the 'ngModel' to be updated properly.
   */
  private blur(target: EventTarget): void {
    if (target instanceof HTMLElement) {
      (target as HTMLElement).blur();
    }
  }

  /**
   * Executes the apply logic.
   */
  private doApply(): boolean {
    if (!this.filterController.canApply()) return false;

    const newData: any = this.filterController.onApply();
    if (newData !== null) {
      this.filterDataChange.emit(newData);
    }
    return true;
  }

  /**
   * Executes the cancel logic.
   */
  private doCancel(): void {
    this.filterController.onCancel();
  }

  /**
   * Executes the clear logic.
   */
  private doClear(): void {
    this.filterController.onClear();
  }

  /**
   * Emits the close event to the consumer with the given context.
   */
  private emitCloseRequest(context?: boolean): void {
    this.closeRequest.emit(context);
  }

  private initFilterController() {
    this.window.setTimeout(() => {
      let component: FilterComponent = this.filterComponent;

      if (!component || !component.createFilterController || !component.filterTemplate) {
        component = this.getFilterComponent(this.filterConfig?.filterType);
      }

      if (component) {
        // Update the controller.
        this.filterController = component.createFilterController({
          document: this.document,
          changeDetectorRef: this.filterChangeDetector
        });
        this.filterController.init({
          column: this.column,
          filterData: this.filterData
        });

        // Update the filter context.  Do this before updating the template since the template references
        // this data context.
        const context = this.filterController.getTemplateContext();

        if (this.instituteSeparationEnabled && this.column.propertyName === 'instituteName') {
          const originalGetItems = this.filterConfig.filterParams.getItems;
          const accessibleInstituteIds = this.accessibleInstitutes.map(institute => institute.id);
          this.filterConfig.filterParams.getItems = () => {
            const items = originalGetItems();
            return items.map(item => ({
              ...item,
              disabled: !accessibleInstituteIds.includes(Number(item.id))
            }));
          };
        }

        this.filterContext = {
          $implicit: context
        };
        // Update the template.
        this.filterTemplate = component.filterTemplate;

        // If the dropdown is active, notify the controller to activate.
        if (this.isActive) {
          this.window.setTimeout(() => {
            this.filterController.onActivate(true);
          });
        }
      }

      // If this column has a custom sort template, use it.
      if (this.column && this.column.sortTemplate) {
        this.sortTemplate = this.column.sortTemplate;
      } else if (this.column && this.column.defaultSort) {
        this.sortTemplate = this.defaultSortTemplate;
      }
    });
  }

  private getFilterComponent(type: string): FilterComponent {
    if (type === 'text') {
      return {
        createFilterController: (context: FilterControllerContext) => {
          return new TextFilterController(context);
        },
        filterTemplate: this.textFilterTemplate
      };
    }

    if (type === 'date') {
      return {
        createFilterController: (context: FilterControllerContext) => {
          return new DateFilterController(context);
        },
        filterTemplate: this.dateFilterTemplate
      };
    }

    if (type === 'set') {
      return {
        createFilterController: (context: FilterControllerContext) => {
          return new SetFilterController(context);
        },
        filterTemplate: this.setFilterTemplate
      };
    }

    if (type === 'range') {
      return {
        createFilterController: (context: FilterControllerContext) => {
          return new RangeFilterController(context);
        },
        filterTemplate: this.rangeFilterTemplate
      };
    }
  }

  /**
   * A change detector implementation passed to the filters.
   */
  filterChangeDetector: FilterChangeDetector;

  /**
   * Specifies the data context for the filter template.
   */
  filterContext: any;

  /**
   * Specifies the template associated with the column's filter config.
   */
  filterTemplate: TemplateType;

  /**
   * Specifies the filter controller associated with the column's filter config.
   */
  filterController: FilterController;

  /**
   * Flag indicating whether or not we need to display a filter section.
   */
  showFilterSection: boolean;

  /**
   * Flag indicating whether or not we need to display a sort section.
   */
  showSortSection: boolean;

  /**
   * Specifies the sort template being used (either the default or custom one).
   */
  sortTemplate: TemplateType;

  /**
   * Specifies whether or not this columns is being sorted ascending or descending or neither (undefined).
   */
  sortedAscending: boolean;

  /**
   * Defined to ease mocking for unit tests.
   */
  private window: Window;
}
